/**
 * Lista de webchats e criação de um novo.
 *
 * O webchat nasce com `localhost` na allowlist e o fluxo-semente do engine (`createStarterFlow`),
 * para o caminho "criar → publicar → colar o snippet" funcionar sem configuração extra.
 *
 * Duas coisas mudaram no desenho, e as duas são de hierarquia.
 *
 * **"Excluir" deixou de ser um botão de texto ao lado de "Abrir".** Lado a lado, com o mesmo tamanho,
 * a ação que apaga o widget de todos os sites do cliente tinha o mesmo peso visual da que abre o
 * editor — e ficava a um clique errado de distância. Virou ícone, à direita, separado por um espaço
 * maior. A confirmação continua sendo a garantia; o desenho é o que evita chegar até ela.
 *
 * **Os metadados desceram um degrau de contraste ao contrário do que se esperaria.** Eram
 * `slate-400` sobre branco, uns 2,9:1 — abaixo dos 4,5:1 que a WCAG pede para texto normal. O que
 * parecia "discreto" era ilegível para quem tem baixa visão, e a hierarquia continua existindo pelo
 * tamanho e pelo peso, que é onde ela deveria estar desde o começo.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDataSource, type WebchatSummary } from '../data/index.js';
import { useAuth } from '../auth/AuthProvider.js';
import {
  Badge,
  Button,
  EmptyState,
  IconButton,
  Input,
  LinkButton,
  Notice,
  PageHeader,
  Section,
} from '../components/ui.js';
import { IconeFluxo, IconeLixeira, IconeMais, IconeSair } from '../components/icons.js';
import { Wordmark } from '../components/Logo.js';
import { isDemoModeLabel } from '../lib/env.js';

function formatDate(value: number | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR');
}

export function ListPage() {
  const [items, setItems] = useState<WebchatSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { user, signOutUser } = useAuth();

  const refresh = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getDataSource().list());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const create = async (): Promise<void> => {
    setCreating(true);
    setError(null);
    try {
      const id = await getDataSource().create(name);
      navigate(`/w/${id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setCreating(false);
    }
  };

  const remove = async (id: string, nome: string): Promise<void> => {
    if (!window.confirm(`Excluir "${nome}"? O widget instalado nos sites para de funcionar.`)) return;
    await getDataSource().remove(id);
    void refresh();
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-6 py-10">
      {/*
        Faixa da marca separada do título da página, como em Vercel e Linear: a primeira diz "em que
        produto estou", a segunda diz "em que tela". Juntas numa linha só, o nome do produto competia
        com o título e nenhum dos dois lia primeiro.
      */}
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <Wordmark />
        <Button variant="ghost" onClick={() => void signOutUser()}>
          <IconeSair />
          Sair
        </Button>
      </div>

      <PageHeader
        title="Seus webchats"
        description={`${user?.email ?? ''}${isDemoModeLabel ? ` · ${isDemoModeLabel}` : ''}`}
      />

      {isDemoModeLabel ? (
        <Notice tone="warn" title="Modo demo">
          Sem Firebase configurado, os dados ficam no <code>localStorage</code> deste navegador. Serve
          para montar e testar fluxos; para valer em produção, preencha{' '}
          <code>apps/admin/.env.local</code>.
        </Notice>
      ) : null}

      {error ? (
        <Notice tone="danger" title="Não foi possível carregar">
          {error}
        </Notice>
      ) : null}

      <Section title="Criar um webchat" description="Ele nasce com um fluxo de exemplo, pronto para editar.">
        {/*
          `<form>` de verdade: Enter no campo cria, que é o que a mão espera depois de digitar um
          nome. Antes só o clique no botão funcionava.
        */}
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!creating) void create();
          }}
        >
          <Input
            value={name}
            aria-label="Nome do novo webchat"
            placeholder="Nome do novo webchat"
            onChange={(event) => setName(event.target.value)}
            className="max-w-sm"
          />
          <Button type="submit" variant="primary" disabled={creating}>
            <IconeMais />
            {creating ? 'Criando…' : 'Criar webchat'}
          </Button>
        </form>
      </Section>

      <Section
        title={loading ? 'Webchats' : `${items.length} ${items.length === 1 ? 'webchat' : 'webchats'}`}
        padded={false}
      >
        {loading ? (
          <p className="p-4 text-sm text-slate-500">Carregando…</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={IconeFluxo}
            title="Nenhum webchat ainda"
            description="Dê um nome no campo acima e crie o primeiro. Leva menos de um minuto até ter um snippet para colar num site."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/w/${item.id}`}
                    className="rounded text-base font-semibold text-slate-900 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                  >
                    {item.name}
                  </Link>
                  <p className="truncate font-mono text-xs text-slate-500">{item.publicKey}</p>
                  <p className="text-xs text-slate-500">editado em {formatDate(item.updatedAt)}</p>
                </div>

                {item.publishedVersion === null ? (
                  <Badge>nunca publicado</Badge>
                ) : (
                  <Badge tone="ok">publicado v{item.publishedVersion}</Badge>
                )}

                {/*
                  Abrir é a ação da linha; excluir é a exceção. Daí o `ml-2` separando as duas: um
                  espaço maior entre elas é o que impede o clique por inércia (Fitts na prática).
                */}
                <LinkButton to={`/w/${item.id}`}>Abrir</LinkButton>
                <IconButton
                  icon={IconeLixeira}
                  label={`Excluir ${item.name}`}
                  variant="danger"
                  className="ml-2"
                  onClick={() => void remove(item.id, item.name)}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
